/**
 * Run table API — fetches flattened solver run data for AG Grid.
 */

import { apiClient } from "./client";

export interface RunTableData {
  columns: { field: string; headerName: string }[];
  rows: Record<string, unknown>[];
  /** Total row count (present when server-side pagination is active). */
  total?: number;
}

export interface RunTableParams {
  projectId: string;
  branch?: string;
  /** Maximum number of rows to return (server-side pagination). */
  limit?: number;
  /** Row offset for server-side pagination. */
  offset?: number;
}

interface ResolvedRunNode {
  id: string;
  path: string;
  kind: string;
  value_json?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  is_stale?: boolean | null;
  stale_reason?: string | null;
  resolved_from?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  source?: string | null;
  code_ref?: unknown;
}

const RUN_METADATA_KEYS = [
  "flow360_folder_id",
  "flow360_folder_name",
  "flow360_project_name",
  "flow360_source_namespace",
  "tsangpo_project",
  "tsangpo_campaign_id",
  "tsangpo_campaign_label",
  "tsangpo_family",
  "tsangpo_phase",
  "tsangpo_phase_index",
  "tsangpo_geometry_variant_id",
  "tsangpo_geometry_variant_label",
  "tsangpo_asset_role",
  "tsangpo_flow360_run_root",
] as const;

function isScalar(value: unknown): value is string | number | boolean | null {
  return value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function firstScalar(...values: unknown[]): string | number | boolean | undefined {
  return values.find((value): value is string | number | boolean => isScalar(value) && value != null && value !== "");
}

function pathLeaf(path: string | null | undefined): string {
  const segments = String(path || "").split(".");
  return segments[segments.length - 1] || String(path || "");
}

function rowFromResolvedRun(node: ResolvedRunNode): Record<string, unknown> {
  const metadata = node.metadata && typeof node.metadata === "object" ? node.metadata : {};
  const valueJson = node.value_json && typeof node.value_json === "object" ? node.value_json : {};
  const leaf = pathLeaf(node.path);
  const displayName = firstScalar(
    valueJson.name,
    metadata.flow360_case_name,
    metadata.case_name,
    metadata.name,
    leaf,
    node.id,
  );
  const row: Record<string, unknown> = {
    name: displayName,
    runId: firstScalar(valueJson.runId, valueJson.run_id, valueJson.case_id, valueJson.flow360_case_id, displayName),
    alpha: firstScalar(valueJson.alpha, valueJson.alpha_deg, valueJson["_extra.operating_condition.alpha"]),
    beta: firstScalar(valueJson.beta, valueJson.beta_deg, valueJson["_extra.operating_condition.beta"]),
    cm: firstScalar(valueJson.cm, valueJson.Cm, valueJson.CM, valueJson.CMy, valueJson.C_m),
    mesh: firstScalar(valueJson.mesh, valueJson.mesh_id, valueJson.tsangpo_geometry_variant_label, valueJson.tsangpo_campaign_id, valueJson.sweep_group),
    design: firstScalar(valueJson.design, valueJson.geometry_id, valueJson.tsangpo_geometry_variant_id, valueJson.tsangpo_family, valueJson.tsangpo_phase),
    family: firstScalar(valueJson.family, valueJson.tsangpo_campaign_id, valueJson.sweep_group, metadata.tsangpo_campaign_id, metadata.flow360_folder_name),
    _node_id: node.id,
    _is_stale: Boolean(node.is_stale),
    _stale_reason: node.stale_reason ?? null,
    _resolved_from: node.resolved_from ?? null,
  };

  if (typeof metadata.sweep_id === "string") row.sweep_group = metadata.sweep_id;
  if (typeof metadata.status === "string") row.status = metadata.status;
  if (typeof metadata.flow360_case_id === "string") row.flow360_case_id = metadata.flow360_case_id;
  if (typeof metadata.base_case_id === "string") row.base_case_id = metadata.base_case_id;
  for (const key of RUN_METADATA_KEYS) {
    const value = metadata[key];
    if (value != null) row[key] = value;
  }
  const configParams = metadata.config_params;
  if (configParams && typeof configParams === "object" && !Array.isArray(configParams)) {
    for (const [key, value] of Object.entries(configParams as Record<string, unknown>)) {
      if (isScalar(value)) row[`cfg_${key}`] = value;
    }
  }

  for (const [key, value] of Object.entries(valueJson)) {
    if (isScalar(value)) row[key] = value;
  }
  if (valueJson.sweep_id && !row.sweep_group) row.sweep_group = valueJson.sweep_id;

  if (node.created_by) row.created_by = node.created_by;
  if (node.created_at) row.created_at = node.created_at;
  if (node.source) row.source = node.source;
  if (node.code_ref) row.code_ref = node.code_ref;

  return row;
}

function tableFromRows(rows: Record<string, unknown>[], total?: number): RunTableData {
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))]
    .sort()
    .map((field) => ({ field, headerName: field }));
  return total == null ? { columns, rows } : { columns, rows, total };
}

async function fetchResolvedRunTableFallback(
  projectId: string,
  branch: string,
  limit?: number,
  offset?: number,
): Promise<RunTableData> {
  const params = new URLSearchParams({
    project_id: projectId,
    branch,
    kind: "run",
    limit: "20000",
  });
  const nodes = await apiClient<ResolvedRunNode[]>(`/nodes?${params.toString()}`);
  const rows = nodes.map(rowFromResolvedRun);
  if (limit == null && offset == null) return tableFromRows(rows);

  const start = offset ?? 0;
  return tableFromRows(rows.slice(start, limit == null ? undefined : start + limit), rows.length);
}

function hasRows(data: RunTableData): boolean {
  return Array.isArray(data.rows) && data.rows.length > 0;
}

export async function fetchRunTable(projectId: string, branch = "main"): Promise<RunTableData> {
  const data = await apiClient<RunTableData>(`/run-table?project_id=${projectId}&branch=${encodeURIComponent(branch)}`);
  return hasRows(data) ? data : fetchResolvedRunTableFallback(projectId, branch);
}

/**
 * Paginated variant — passes optional `limit` and `offset` to the backend.
 * Falls back to the full table when neither is provided.
 */
export async function fetchRunTablePaginated({ projectId, branch = "main", limit, offset }: RunTableParams): Promise<RunTableData> {
  const params = new URLSearchParams({ project_id: projectId, branch });
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const data = await apiClient<RunTableData>(`/run-table?${params.toString()}`);
  return hasRows(data) ? data : fetchResolvedRunTableFallback(projectId, branch, limit, offset);
}
